import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { adminApi, type AdminHomeResponse, type UpdateHomeOnboardingInput } from "services/adminApi"
import { useAppDispatch, useAppSelector } from "hooks/storeHooks"
import { selectUserInfo } from "store/user/userSlice"
import { addSelectedCategory, setSelectedCategories } from "store/product/productSlice"
import { UserStatusTypes } from "types"

export const HOME_QUERY_KEY = ["home", "self"] as const

/**
 * Home data drives the page AND the sidebar categories, so it loads for every
 * ACTIVE user. `refetchOnMount: "always"` keeps the counters honest when the
 * merchant returns from an add-product flow.
 */
export const useHomeQuery = () => {
  const userInfo = useAppSelector(selectUserInfo)
  const enabled = Boolean(userInfo?.username) && userInfo?.status === UserStatusTypes.ACTIVE

  return useQuery({
    queryKey: HOME_QUERY_KEY,
    queryFn: () => adminApi.getHome(),
    enabled,
    refetchOnMount: "always",
    retry: false,
  })
}

export const useUpdateOnboardingMutation = () => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()

  return useMutation({
    mutationFn: (input: UpdateHomeOnboardingInput) => adminApi.updateHomeOnboarding(input),
    onMutate: async (input) => {
      const category = input.selectCategory
      if (!category) return
      // Optimistic: the category must reach the sidebar/guard state BEFORE the
      // caller navigates to the add-product flow, or CategoryAccessLayout
      // bounces the navigation back to /home.
      dispatch(addSelectedCategory(category))
      await queryClient.cancelQueries({ queryKey: HOME_QUERY_KEY })
      queryClient.setQueryData<AdminHomeResponse>(HOME_QUERY_KEY, (previous) =>
        previous && !previous.onboarding.selectedCategories.includes(category)
          ? {
              ...previous,
              onboarding: {
                ...previous.onboarding,
                selectedCategories: [...previous.onboarding.selectedCategories, category],
              },
            }
          : previous
      )
    },
    onSuccess: (result) => {
      dispatch(setSelectedCategories(result.onboarding.selectedCategories))
      queryClient.setQueryData<AdminHomeResponse>(HOME_QUERY_KEY, (previous) =>
        previous ? { ...previous, onboarding: result.onboarding } : previous
      )
      queryClient.invalidateQueries({ queryKey: HOME_QUERY_KEY, refetchType: "none" })
    },
    onError: () => {
      // Roll back the optimistic sidebar state from the server truth.
      void queryClient.invalidateQueries({ queryKey: HOME_QUERY_KEY })
    },
  })
}
